import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Cropper from "react-easy-crop";
import { GrGroup } from "react-icons/gr";
import { FaWhatsapp, FaRegFilePdf } from "react-icons/fa";
import { jsPDF } from "jspdf";
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import InputModal from "../../Component/Common/ConstomModal/InputModal";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import SwitchField from "../../Component/Common/SwitchField";
import {
  addFullvoterValidation,
  editFullvoterValidation,
} from "../../helper/errorSchema";
import { checkFormError, handleKeyDown, hasAccess, FEATURES } from "../../helper";
import { getStorage } from "../../helper/storage";
import {
  addFullVoter,
  deleteFullVoter,
  getFamilyDetails,
  singleFullVoter,
  updateFullVoter,
} from "../../services/voterService";
import { getCastProfession } from "../../services/serviceCastPro";
import { colorLIst, colourLabel, findOption, getOptionValue, uniqueOptions, votedLabel } from "./components/voterHelpers";
import { invalidateVoterCache, refreshCachedVoter, removeCachedVoter } from "./components/voterCache";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PLACEHOLDER_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' fill='%23eef2f7'/%3E%3Ccircle cx='80' cy='61' r='31' fill='%2395a3b8'/%3E%3Cpath d='M28 139c8-32 29-49 52-49s44 17 52 49' fill='%2395a3b8'/%3E%3C/svg%3E";
const LANGUAGE_STORAGE_KEY = "electioneasy_voter_form_language";
const ALL_LANGUAGE_VALUES = ["both", "english", "hindi"];

// "app_available_languages" is populated by ProtectRoutes.js from the
// app-details API's `language_supported` field - mirrors Sidebar.js's
// getAvailableLanguages() so the form toggle only offers languages this app
// actually supports instead of always showing Both/English/Hindi. No static
// fallback here either: an empty/missing value means language_supported
// came back null, not "assume all three".
const getAvailableLanguageValues = () => {
  try {
    const stored = localStorage.getItem("app_available_languages");
    if (stored) {
      const parsed = JSON.parse(stored);
      return ALL_LANGUAGE_VALUES.filter((value) => parsed.includes(value));
    }
  } catch {
    // ignore parse errors
  }
  return [];
};

// The form still needs one concrete language to decide which fields to show
// even when nothing is configured - "both" (show every field) is the safest
// degraded state, not a static list presented as if it came from the API.
const getStoredVoterLanguage = () => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  const available = getAvailableLanguageValues();
  if (available.includes(stored)) return stored;
  return available[0] ?? "both";
};

const orderObj = {
  sr_no: "",
  assembly_no: "",
  booth_no: "",
  epic_no: "",
  voter_sr_number: "",
  house_no: "",
  age: "",
  full_name_e: "",
  full_name_h: "",
  first_name_e: "",
  first_name_h: "",
  middle_name_e: "",
  middle_name_h: "",
  last_name_e: "",
  last_name_h: "",
  gender_e: "",
  gender_h: "",
  address_e: "",
  address_h: "",
  booth_address_e: "",
  booth_address_h: "",
  mobile_no: "",
  colour: "",
  caste: "",
  profession: "",
  updated_address: "",
  post: "",
  voter_type: 2,
  is_voted: false,
  is_activist: false,
  address_change: "",
  voter_lat: "",
  voter_long: "",
  booth_lat: "",
  booth_long: "",
  extra1: "",
  extra2: "",
  extra3: "",
  extra4: "",
  updated_by: "",
  cast_no: "",
  family_id: "",
  family_head: "",
  voter_status: "",
};

// The `disabled` reason for most fields is EDIT_VOTER access + read-only
// (view) mode; a handful (mobile/colour/profession/post/caste/
// updated_address/checkboxes) are only ever disabled by view mode. Encoding
// that here keeps the 24-field form data-driven instead of 24 near-identical
// JSX blocks (each was previously identical apart from name/label/maxLength/
// onKeyDown) - ported verbatim from FullVoter.js's Add/Edit offcanvas.
// `section` maps each field onto the 5 progress-rail steps of the redesigned
// layout and also fixes the display order within that section (the array
// order is the render order).
const TEXT_FIELDS = [
  { name: "epic_no", label: "Epic Number", section: "identification", requireEditAccess: true },
  { name: "sr_no", label: "Sr Number", section: "identification", requireEditAccess: true },
  { name: "voter_sr_number", label: "Voter Serial Number", section: "identification", requireEditAccess: true },
  { name: "assembly_no", label: "Assembly Number", maxLength: 4, onKeyDown: handleKeyDown, section: "identification", requireEditAccess: true },
  { name: "booth_no", label: "Booth Number", section: "identification", requireEditAccess: true },
  { name: "house_no", label: "House Number", section: "identification", requireEditAccess: true },

  { name: "full_name_e", label: "Full Name English", lang: "en", section: "voter-details", requireEditAccess: true },
  { name: "full_name_h", label: "Full Name Hindi", lang: "hi", section: "voter-details", requireEditAccess: true },
  { name: "first_name_e", label: "First Name English", lang: "en", section: "voter-details", requireEditAccess: true },
  { name: "first_name_h", label: "First Name Hindi", lang: "hi", section: "voter-details", requireEditAccess: true },
  { name: "middle_name_e", label: "Middle Name English", lang: "en", section: "voter-details", requireEditAccess: true },
  { name: "middle_name_h", label: "Middle Name Hindi", lang: "hi", section: "voter-details", requireEditAccess: true },
  { name: "last_name_e", label: "Last Name English", lang: "en", section: "voter-details", requireEditAccess: true },
  { name: "last_name_h", label: "Last Name Hindi", lang: "hi", section: "voter-details", requireEditAccess: true },
  { name: "gender_e", label: "Gender English", lang: "en", section: "voter-details", requireEditAccess: true },
  { name: "gender_h", label: "Gender Hindi", lang: "hi", section: "voter-details", requireEditAccess: true },
  { name: "age", label: "Age", maxLength: 2, onKeyDown: handleKeyDown, section: "voter-details", requireEditAccess: true },

  { name: "address_e", label: "Address English", lang: "en", section: "address", requireEditAccess: true },
  { name: "address_h", label: "Address Hindi", lang: "hi", section: "address", requireEditAccess: true },
  { name: "booth_address_e", label: "Both Address English", lang: "en", section: "address", requireEditAccess: true },
  { name: "booth_address_h", label: "Both Address Hindi", lang: "hi", section: "address", requireEditAccess: true },
  { name: "address_change", label: "Address Change", placeholder: "Enter Address Change Details", lang: "en", section: "address", requireEditAccess: true },

  { name: "voter_lat", label: "Voter Latitude", section: "location", requireEditAccess: true },
  { name: "voter_long", label: "Voter Longitude", section: "location", requireEditAccess: true },
  { name: "booth_lat", label: "Booth Latitude", section: "location", requireEditAccess: true },
  { name: "booth_long", label: "Booth Longitude", section: "location", requireEditAccess: true },

  { name: "extra1", label: "Extra 1", section: "additional", requireEditAccess: true },
  { name: "extra2", label: "Extra 2", section: "additional", requireEditAccess: true },
  { name: "extra3", label: "Extra 3", section: "additional", requireEditAccess: true },
  { name: "extra4", label: "Extra 4", section: "additional", requireEditAccess: true },
];

// The 5 progress-rail steps in the redesigned Add/Edit Voter layout. Mobile
// Number/Updated Address/the Colour-Profession-Post-Caste dropdowns/the
// Activist-Voted checkboxes aren't in TEXT_FIELDS (different components or
// special rendering) but are placed into these same sections in the JSX.
const SECTIONS = [
  { id: "identification", label: "Identification" },
  { id: "voter-details", label: "Voter Details" },
  { id: "address", label: "Address" },
  { id: "location", label: "Location" },
  { id: "additional", label: "Additional" },
];

// The Add Voter form/offcanvas from FullVoter.js, moved to its own routed
// page. Also handles Edit and read-only View for existing voters (reached
// via VoterTable's row actions as /voter-management/add?editId=<id> and
// &view=1) - editing reuses ~90% of the same form, so it isn't duplicated
// into a second page.
export const AddVoter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");
  const isViewMode = searchParams.get("view") === "1";
  const userLogin = getStorage("logged-in");

  const [formOrderValue, setFormOrderValue] = useState(orderObj);
  const [formOrderName, setFormOrderName] = useState({});
  const [initialForm, setInitialForm] = useState(orderObj);
  // Raw single-voter response, for fields the form has no input for.
  const [voterRecord, setVoterRecord] = useState(null);
  const [formError, setFormError] = useState({});
  const [isEdit, setIsEdit] = useState(!!editId);
  const [fullView, setFullView] = useState(isViewMode);
  const [updateId, setUpdateId] = useState("");
  const [epicNo, setEpicNo] = useState("");
  const [isLoadding, setIsLoadding] = useState(false);

  const [castList, setCastList] = useState([]);
  const [profList, setProfList] = useState([]);
  const [postList, setPostList] = useState([]);

  const fileRef = useRef(null);
  const [preview, setPreview] = useState(PLACEHOLDER_AVATAR);
  const [profileImageError, setProfileImageError] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [formLang, setFormLang] = useState(getStoredVoterLanguage);
  const showEn = formLang === "english" || formLang === "both";
  const showHi = formLang === "hindi" || formLang === "both";

  const [showModal, setShowModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [whatsappError, setWhatsappError] = useState({});

  const [openDel, setOpenDel] = useState(false);

  const avfBodyRef = useRef(null);
  const avfSectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  // While a click-driven scroll is in flight, ignore the scroll handler's own
  // active-section guess: on a short form the target section's offsetTop can
  // exceed the container's max scrollTop (browser clamps the scroll to the
  // bottom), which would otherwise make the "at bottom" bottom-section
  // override below stomp on the section the user actually clicked.
  const avfScrollLockRef = useRef(false);
  const avfScrollLockTimeoutRef = useRef(null);

  useEffect(() => () => window.clearTimeout(avfScrollLockTimeoutRef.current), []);

  const registerSectionRef = (id) => (el) => {
    avfSectionRefs.current[id] = el;
  };

  const scrollToSection = (id) => {
    const container = avfBodyRef.current;
    const target = avfSectionRefs.current[id];
    if (!container || !target) return;
    avfScrollLockRef.current = true;
    window.clearTimeout(avfScrollLockTimeoutRef.current);
    avfScrollLockTimeoutRef.current = window.setTimeout(() => {
      avfScrollLockRef.current = false;
    }, 600);
    container.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    setActiveSection(id);
  };

  const handleAvfBodyScroll = () => {
    if (avfScrollLockRef.current) return;
    const container = avfBodyRef.current;
    if (!container) return;
    // Scrolled to (or within a few px of) the bottom: the last section's own
    // offsetTop can be greater than any scrollTop this container can reach
    // (it doesn't have enough trailing content below it to scroll past its
    // own top), so the threshold loop below would never select it - force it.
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
    if (atBottom) {
      setActiveSection(railSections[railSections.length - 1].id);
      return;
    }
    const scrollPos = container.scrollTop + 48;
    let current = railSections[0].id;
    railSections.forEach((section) => {
      const target = avfSectionRefs.current[section.id];
      if (target && target.offsetTop <= scrollPos) current = section.id;
    });
    setActiveSection(current);
  };

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const nextLanguage = event?.detail ?? getStoredVoterLanguage();
      if (getAvailableLanguageValues().includes(nextLanguage)) setFormLang(nextLanguage);
    };
    // appLanguagesUpdated fires when ProtectRoutes.js refreshes
    // "app_available_languages" from the API - re-derive formLang in case
    // the previously-selected language is no longer supported.
    const handleAvailableLanguagesChange = () => setFormLang(getStoredVoterLanguage());
    window.addEventListener("storage", handleLanguageChange);
    window.addEventListener("voterLanguageChange", handleLanguageChange);
    window.addEventListener("appLanguagesUpdated", handleAvailableLanguagesChange);
    return () => {
      window.removeEventListener("storage", handleLanguageChange);
      window.removeEventListener("voterLanguageChange", handleLanguageChange);
      window.removeEventListener("appLanguagesUpdated", handleAvailableLanguagesChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const getCastAndProList = async () => {
      const res = await getCastProfession();
      const records = Array.isArray(res?.results) ? res.results : [];
      setCastList(uniqueOptions(records.map((item) => item?.caste_english ?? item?.caste ?? item?.cast)));
      setProfList(
        uniqueOptions(records.map((item) => item?.profession_english ?? item?.profession ?? item?.profession_name))
      );
      setPostList(uniqueOptions(records.map((item) => item?.post_english ?? item?.post ?? item?.post_name)));
    };
    getCastAndProList();
  }, []);

  // Close back to the page that opened this form (Dashboard, Voter Search,
  // Age Wise, Alphabetical - passed as location.state.from), falling back to
  // Voter Search when opened directly (sidebar "Add Voter", a pasted URL).
  const location = useLocation();
  const returnTo = location.state?.from || "/voter-management/search";
  const goBackToSearch = () => navigate(returnTo);

  const getOrderDetails = useMutation(singleFullVoter, {
    onSuccess: (res) => {
      if (!res?.success) return;
      // The single-voter GET returns the record's fields flattened on the
      // response itself (res.sr_no, res.assembly_no, ...) - unlike the list
      // endpoint, there's no res.results wrapper here. Reading res.results.*
      // (as this used to) silently produced undefined for every field, which
      // is why View/Edit looked completely empty regardless of the record.
      const record = res || {};
      const Details = {
        sr_no: record.sr_no,
        cast_no: record.cast_no ?? record.sr_no ?? "",
        assembly_no: record.assembly_no,
        booth_no: record.booth_no,
        epic_no: record.epic_no,
        voter_sr_number: record.voter_sr_number ?? record.column_id,
        house_no: record.house_no,
        age: record.age,
        full_name_e: record.full_name_e,
        full_name_h: record.full_name_h,
        first_name_e: record.first_name_e,
        first_name_h: record.first_name_h,
        middle_name_e: record.middle_name_e,
        middle_name_h: record.middle_name_h,
        last_name_e: record.last_name_e,
        last_name_h: record.last_name_h,
        gender_e: record.gender_e,
        gender_h: record.gender_h,
        address_e: record.address_e,
        address_h: record.address_h,
        booth_address_e: record.booth_address_e,
        booth_address_h: record.booth_address_h,
        mobile_no: record.mobile_no,
        colour: record.colour,
        caste: record.caste ?? record.cast ?? record.caste_english ?? "",
        profession: record.profession,
        updated_address: record.updated_address,
        post: record.post,
        app_name: record.app_name,
        party_name: record.party_name,
        voter_type: record.voter_type,
        is_voted: record.is_voted,
        is_activist: record.is_activist,
        address_change: record.address_change,
        voter_lat: record.voter_lat,
        voter_long: record.voter_long,
        booth_lat: record.booth_lat,
        booth_long: record.booth_long,
        extra1: record.extra1,
        extra2: record.extra2,
        extra3: record.extra3,
        extra4: record.extra4,
        updated_by: record.updated_by,
        profile_image: record.profile_image,
        family_id: record.family_id,
        family_head: record.family_head,
        voter_status: record.voter_status,
      };
      setFormOrderValue(Details);
      setInitialForm(Details);
      setVoterRecord(record);
      setPreview(record.profile_image || PLACEHOLDER_AVATAR);

      const common = (name, id) => {
        if (name === "colour") {
          const details = colorLIst?.find((i) => i.value === id);
          return details;
        }
        if (name === "caste") return findOption(castList, id);
        if (name === "post") return findOption(postList, id);
        if (name === "profession") return findOption(profList, id);
        return null;
      };

      setFormOrderName((current) => ({
        ...current,
        colour: common("colour", record.colour),
        caste: common("caste", Details.caste),
        profession: common("profession", record.profession),
        post: common("post", record.post),
      }));

      setIsEdit(true);
      setFormError({});
      setUpdateId(record.id);
      setEpicNo(record.epic_no || "");
    },
  });

  useEffect(() => {
    if (editId) {
      getOrderDetails.mutate({ id: editId, app_name: userLogin?.app_name, party_name: userLogin?.party_name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, castList, profList, postList]);

  const handleOrderChange = (name, value) => {
    setFormOrderValue((current) => ({ ...current, [name]: value }));
  };

  const onSuccessResponse = (message) => {
    toast.success(message || "success");
    goBackToSearch();
  };

  // Keep the downloaded voter list (voterCache) in step with this change.
  const addMutation = useMutation(addFullVoter, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) {
        invalidateVoterCache(); // a new voter can't be slotted in locally
        onSuccessResponse(res?.message);
      }
    },
  });

  const updateMutation = useMutation(updateFullVoter, {
    onSuccess: (res) => {
      setIsLoadding(false);
      if (res?.success) {
        refreshCachedVoter({ id: updateId, app_name: userLogin?.app_name, party_name: userLogin?.party_name });
        onSuccessResponse(res?.message);
      }
    },
  });

  const deleteMutation = useMutation(deleteFullVoter, {
    onSuccess: (res) => {
      if (res?.success) {
        removeCachedVoter(updateId);
        onSuccessResponse(res?.message);
      }
    },
  });

  const handleAdd = (formData) => addMutation.mutate(formData);

  const handleUpdate = (data) => {
    const cleanData = { ...data };
    delete cleanData.voter_type;
    const formData = new FormData();
    Object.entries(cleanData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") formData.append(key, value);
    });
    updateMutation.mutate({ formdata: formData, id: updateId });
  };

  const onOrderSubmit = async () => {
    const validationResult = await checkFormError(
      { ...formOrderValue },
      formOrderValue.voter_type === 2 ? addFullvoterValidation : editFullvoterValidation
    );

    const payload = {
      sr_no: formOrderValue.sr_no,
      assembly_no: formOrderValue?.assembly_no,
      booth_number: formOrderValue?.booth_no,
      epic_number: formOrderValue?.epic_no,
      voter_sr_number: formOrderValue?.voter_sr_number,
      house_number: formOrderValue?.house_no,
      age: formOrderValue?.age,
      full_name_english: formOrderValue?.full_name_e,
      first_name_english: formOrderValue?.first_name_e,
      middle_name_english: formOrderValue?.middle_name_e,
      last_name_english: formOrderValue?.last_name_e,
      gender_english: formOrderValue?.gender_e,
      address_english: formOrderValue?.address_e,
      address_hindi: formOrderValue?.address_h,
      booth_address_english: formOrderValue?.booth_address_e,
      booth_address_hindi: formOrderValue?.booth_address_h,
      mobile_no: formOrderValue?.mobile_no,
      colour: getOptionValue(formOrderValue?.colour),
      caste: getOptionValue(formOrderValue?.caste),
      profession: getOptionValue(formOrderValue?.profession),
      updated_address: formOrderValue?.updated_address,
      post: getOptionValue(formOrderValue?.post),
      voter_type: 2,
      is_voted: formOrderValue?.is_voted,
      is_activist: formOrderValue?.is_activist,
      cast_no: formOrderValue?.cast_no,
      address_change: formOrderValue?.address_change,
      voter_lat: formOrderValue?.voter_lat,
      voter_long: formOrderValue?.voter_long,
      booth_lat: formOrderValue?.booth_lat,
      booth_long: formOrderValue?.booth_long,
      extra1: formOrderValue?.extra1,
      extra2: formOrderValue?.extra2,
      extra3: formOrderValue?.extra3,
      extra4: formOrderValue?.extra4,
      updated_by: formOrderValue?.updated_by,
    };

    if (userLogin?.app_name) payload.app_name = userLogin.app_name;
    if (userLogin?.party_name) payload.party_name = userLogin.party_name;

    const payloadUpdate = { ...payload };
    Object.keys(formOrderValue).forEach((item) => {
      if (formOrderValue[item] !== initialForm[item]) payloadUpdate[item] = formOrderValue[item];
    });

    if (validationResult === true) {
      setIsLoadding(true);
      if (isEdit) {
        handleUpdate(payloadUpdate);
      } else {
        handleAdd(payloadUpdate);
      }
    } else {
      setIsLoadding(false);
      setFormError(validationResult);
    }
  };

  // --- Profile photo upload/crop --------------------------------------------

  const validateImage = (file) => {
    if (!file) return "";
    if (!IMAGE_TYPES.includes(file.type)) return "Only JPG, PNG, or WEBP images are allowed";
    if (file.size > MAX_IMAGE_SIZE) return "Image size must be 2 MB or less";
    return "";
  };

  const handleProfileFileSelect = (file) => {
    const error = validateImage(file);
    if (error) {
      setProfileImageError(error);
      return;
    }
    setProfileImageError("");
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, areaPixels) => setCroppedAreaPixels(areaPixels);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });

  const getCroppedImageBlob = async (imageSrc, cropAreaPixels, outputSize = 480) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      cropAreaPixels.x,
      cropAreaPixels.y,
      cropAreaPixels.width,
      cropAreaPixels.height,
      0,
      0,
      outputSize,
      outputSize
    );
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92));
  };

  const getImageDataUrl = async (imageSrc) => {
    if (!imageSrc || imageSrc === PLACEHOLDER_AVATAR) return "";
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.9);
    } catch (error) {
      return "";
    }
  };

  const handleCropConfirm = async () => {
    if (!rawImageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedImageBlob(rawImageSrc, croppedAreaPixels);
    const croppedFile = new File([blob], "profile.jpg", { type: "image/jpeg" });
    const nextPreview = URL.createObjectURL(blob);
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return nextPreview;
    });
    setFormOrderValue((current) => ({ ...current, profile_image: croppedFile }));
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const removeProfileImage = () => {
    setPreview((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return PLACEHOLDER_AVATAR;
    });
    setFormOrderValue((current) => ({ ...current, profile_image: "", remove_profile_image: true }));
    if (fileRef.current) fileRef.current.value = "";
  };

  // --- Family details --------------------------------------------------------

  const getFamilyDetail = useMutation(getFamilyDetails, {
    onSuccess: (res) => {
      if (res?.success) {
        const formattedData = res.results.data.map((item) => ({
          sr_no: item.sr_no,
          full_name_e: item.full_name_e,
          age: item.age,
          gender_e: item.gender_e,
          house_no: item.house_no,
          assembly_no: item.assembly_no,
          booth_no: item.booth_no,
          epic_no: item.epic_no,
          party_name: item.party_name,
          address_e: item.address_e,
          mobile_no: item.mobile_no,
        }));
        navigate("/Family", { state: formattedData });
      }
    },
  });

  const handleFamilyAction = () => {
    getFamilyDetail.mutate({ house_no: formOrderValue?.house_no, address_e: formOrderValue?.address_e });
  };

  // --- Single-voter WhatsApp share / PDF -------------------------------------

  const handleWhatsappSubmit = () => {
    if (!mobileNumber) {
      setWhatsappError({ mobileNumber: "Please enter a valid mobile number" });
      return;
    }
    const voter = formOrderValue;
    const message = `
Vote For BJP
Booth No : ${voter.booth_no}     Sr No : ${voter.sr_no}
Name : ${voter.full_name_h}
Relative Name : ${voter.last_name_h}
Gender/Age : ${voter.gender_e}/${voter.age}
Address : ${voter.house_no} - ${voter.address_h || "N/A"}
Polling Booth : ${voter.booth_address_h || "N/A"}

--------------------------------------------`;
    const footerMessage = `यह आपकी वोटर स्लिप है इसे दिखा कर 20 नवंबर बुधवार को भारतीय जनता पार्टी के टेबल से आप अपनी वोटर स्लिप लेकर आप अपना कीमती वोट डाल सकते है
हर वोट जरूरी है कृपया वोट जरूर दे

आपका सुरेश अवस्थी (प्रत्याशी 77-लोकसभा वाराणसी)
भा जा पा को वोट दे`;
    const fullMessage = `${message}\n\n${footerMessage}`;
    window.open(`https://wa.me/${mobileNumber}?text=${encodeURIComponent(fullMessage)}`, "_blank");
    setShowModal(false);
    setMobileNumber("");
  };

  const addPdfHeader = (doc, y = 12) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`${formOrderValue?.assembly_no || "-"} Assembly`, 14, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Booth No. ${formOrderValue?.booth_no || "-"}`, 14, y + 7);
    doc.text(`Party: ${userLogin?.party_name || "-"}`, 14, y + 14);
    doc.text(
      `Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}`,
      14,
      y + 21
    );
  };

  const handleGenerateSinglePDF = async () => {
    const voter = formOrderValue;
    const doc = new jsPDF();
    const data = {
      name: voter.full_name_e || voter.full_name_h || "-",
      relativeName: voter.last_name_e || voter.last_name_h || "-",
      mobile: voter.mobile_no || "-",
      epic: voter.epic_no || "-",
      assembly: voter.assembly_no || "-",
      booth: voter.booth_no || "-",
      house: voter.house_no || "-",
      age: voter.age || "-",
      gender: voter.gender_e || voter.gender_h || "-",
      profession: voter.profession || "-",
      post: voter.post || "-",
      address: voter.updated_address || voter.address_e || voter.address_h || "-",
      colour: colourLabel(voter.colour),
      votingStatus: votedLabel(voter.is_voted),
      votingFor: voter.party_name || userLogin?.party_name || "-",
      photo: voter.profile_image || preview,
    };
    addPdfHeader(doc, 12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("VOTER DETAILS", 14, 48);
    doc.setDrawColor(173, 140, 6);
    doc.setLineWidth(0.8);
    doc.line(14, 52, 196, 52);

    const photoDataUrl = await getImageDataUrl(data.photo);
    doc.setDrawColor(210, 218, 230);
    doc.roundedRect(14, 60, 40, 40, 3, 3);
    if (photoDataUrl) {
      doc.addImage(photoDataUrl, "JPEG", 14, 60, 40, 40);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Profile Photo", 23, 82);
    }

    const details = [
      ["Name", data.name],
      ["Relative Name", data.relativeName],
      ["Mobile Number", data.mobile],
      ["EPIC Number", data.epic],
      ["Assembly Number", data.assembly],
      ["Booth Number", data.booth],
      ["House Number", data.house],
      ["Age", data.age],
      ["Gender", data.gender],
      ["Profession", data.profession],
      ["Post", data.post],
      ["Address", data.address],
    ];
    let y = 62;
    details.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`${label}:`, 62, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value), 104, y, { maxWidth: 88 });
      y += label === "Address" ? 12 : 7;
    });

    y = Math.max(y + 6, 116);
    doc.setFillColor(247, 249, 252);
    doc.roundedRect(14, y, 182, 48, 4, 4, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Voting Information", 20, y + 10);
    doc.setFontSize(10);
    doc.text("Voting Status:", 20, y + 22);
    doc.text("Color:", 20, y + 32);
    doc.text("Political Preference:", 20, y + 42);
    doc.setFont("helvetica", "normal");
    doc.text(data.votingStatus, 68, y + 22);
    doc.text(data.colour, 68, y + 32);
    doc.text(`Voting For: ${data.votingFor}`, 68, y + 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Generated by ElectionEasy", 14, 286);
    doc.save("VoterDetails.pdf");
  };

  const handleDelete = () => {
    deleteMutation.mutate({ id: updateId, app_name: userLogin?.app_name, party_name: userLogin?.party_name });
  };

  // Every field is editable (subject to EDIT_VOTER access) whether the
  // voter is brand-new or already exists - only View mode (fullView) is
  // actually read-only.
  const editDisabled = !hasAccess(userLogin?.user_type, FEATURES.EDIT_VOTER) || fullView;

  // View/Edit only show fields the record actually has a value for (a blank
  // electoral-roll form field isn't useful once a real record is loaded).
  // Checked against initialForm (the value as loaded from the API), not the
  // live formOrderValue, so a field doesn't pop in/out as the user edits
  // other fields. Doesn't apply to a brand-new voter (isEdit false) - there
  // every field is legitimately empty and still needs to be fillable.
  const hasInitialValue = (name) => {
    const value = initialForm?.[name];
    return value !== null && value !== undefined && String(value).trim() !== "";
  };
  const isExistingRecord = isEdit;
  const isFamilyHead = Number(formOrderValue?.family_head) === 1;

  // "additional" always has the Activist/Voted toggles (booleans always
  // render, false is a real value), so it's never emptied out by the hide
  // rule above - only sections built entirely from TEXT_FIELDS need this
  // check, otherwise their heading is left dangling over an empty grid.
  const sectionHasContent = (sectionId) => {
    if (!isExistingRecord) return true;
    if (sectionId === "additional") return true;
    if (TEXT_FIELDS.some((field) => field.section === sectionId && hasInitialValue(field.name))) return true;
    if (sectionId === "voter-details") return hasInitialValue("mobile_no");
    if (sectionId === "address") return hasInitialValue("updated_address");
    return false;
  };

  // "Other Details": every field in the loaded record that has data but
  // isn't shown anywhere above (no input for it in this form), read-only, so
  // View/Edit never hide data the API returned. Fields the language toggle
  // hides on purpose (English/Hindi) are left out, as are internal keys.
  const otherDetails = useMemo(() => {
    if (!voterRecord) return [];
    const shownElsewhere = new Set([
      ...TEXT_FIELDS.map((field) => field.name),
      "mobile_no", "updated_address", "colour", "caste", "cast", "caste_english",
      "profession", "post", "is_voted", "is_activist", "profile_image",
      "family_id", "voter_status", "family_head", "voter_type",
    ]);
    const internal = new Set(["id", "uuid", "success", "status", "message", "results"]);
    return Object.entries(voterRecord)
      .filter(([key, value]) => {
        if (shownElsewhere.has(key) || internal.has(key)) return false;
        if (value === null || value === undefined || typeof value === "object") return false;
        return String(value).trim() !== "";
      })
      .map(([key, value]) => ({
        name: key,
        label: key
          .replace(/_/g, " ")
          .replace(/(\D)(\d+)$/, "$1 $2")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        value:
          typeof value === "boolean"
            ? value ? "Yes" : "No"
            : /^\d{4}-\d{2}-\d{2}T/.test(String(value))
            ? new Date(value).toLocaleString()
            : String(value),
      }));
  }, [voterRecord]);

  const railSections = otherDetails.length
    ? [...SECTIONS, { id: "other", label: "Other Details" }]
    : SECTIONS;

  // Drives the footer's "N of M required fields need attention" note.
  // Mobile Number is required but rendered outside TEXT_FIELDS (special +91
  // prefix markup), so it's counted in alongside the data-driven fields.
  const visibleRequiredFields = TEXT_FIELDS.filter((field) => {
    if (field.lang === "en" && !showEn) return false;
    if (field.lang === "hi" && !showHi) return false;
    return true;
  });
  const totalRequiredFields = visibleRequiredFields.length + 1;
  const requiredErrorCount =
    visibleRequiredFields.filter((field) => !!formError?.[field.name]).length + (formError?.mobile_no ? 1 : 0);

  return (
    <>
      <Modal isOpen toggle={goBackToSearch} centered className="avf-dialog" contentClassName="avf-content">
        <div className="avf-header">
          <div className="avf-header-top">
            <div>
              <h2 className="avf-title">{fullView ? "View Voter" : isEdit ? "Edit Voter" : "Add Voter"}</h2>
              <p className="avf-subtitle">
                {fullView
                  ? "Voter record details (read-only)"
                  : "Fill in the voter's details across the sections below"}
              </p>
            </div>
            <button type="button" className="avf-close-btn" onClick={goBackToSearch} aria-label="Close">
              ×
            </button>
          </div>
          <div className="avf-rail">
            {railSections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                className={`avf-step ${activeSection === section.id ? "is-active" : ""}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="avf-step-num">{index + 1}</span>
                <span className="avf-step-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="avf-body" ref={avfBodyRef} onScroll={handleAvfBodyScroll}>
          <div className="avf-photo-card">
            <button
              type="button"
              className="avf-photo-avatar"
              onClick={() => setPreviewModalOpen(true)}
              disabled={!preview || preview === PLACEHOLDER_AVATAR}
              title="View full photo"
            >
              <img
                src={preview || ""}
                alt="Profile preview"
                onError={({ currentTarget }) => {
                  currentTarget.src = PLACEHOLDER_AVATAR;
                }}
              />
            </button>
            <div className="avf-photo-info">
              <div className="avf-photo-title">Voter photograph</div>
              <div className="avf-photo-subtitle">JPG or PNG, square crop recommended, up to 2MB</div>
              {profileImageError && <div className="voter-photo-error">{profileImageError}</div>}
              {isExistingRecord && (formOrderValue?.family_id || formOrderValue?.voter_status || isFamilyHead) && (
                <div className="avf-meta-badges">
                  {isFamilyHead && <span className="avf-badge avf-badge-head">Head</span>}
                  {formOrderValue?.voter_status && (
                    <span className="avf-badge avf-badge-status">{formOrderValue.voter_status}</span>
                  )}
                  {formOrderValue?.family_id && (
                    <span className="avf-badge avf-badge-family">Family ID: {formOrderValue.family_id}</span>
                  )}
                </div>
              )}
            </div>
            <div className="avf-photo-actions">
              {preview && preview !== PLACEHOLDER_AVATAR && (
                <button type="button" className="avf-photo-remove-btn" onClick={removeProfileImage} disabled={fullView}>
                  Remove
                </button>
              )}
              <button
                type="button"
                className="avf-photo-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={fullView}
              >
                {preview && preview !== PLACEHOLDER_AVATAR ? "Change photo" : "Upload photo"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                hidden
                onChange={({ target: { files } }) => handleProfileFileSelect(files?.[0])}
              />
            </div>
          </div>

          {SECTIONS.map((section, index) => {
            if (!sectionHasContent(section.id)) return null;
            return (
            <div key={section.id} className="avf-section" ref={registerSectionRef(section.id)}>
              <div className="avf-section-head">
                <span className="avf-section-num">{index + 1}</span>
                <h3 className="avf-section-title">{section.label}</h3>
              </div>
              <div className="avf-grid">
                {TEXT_FIELDS.filter((field) => field.section === section.id).map((field) => {
                  if (field.lang === "en" && !showEn) return null;
                  if (field.lang === "hi" && !showHi) return null;
                  if (isExistingRecord && !hasInitialValue(field.name)) return null;
                  return (
                    <div className="avf-field" key={field.name}>
                      <InputField
                        floating
                        className="modal_input orderModal_Input"
                        label={field.label}
                        labelClass="FormLableKaran"
                        placeholder={field.placeholder || `Enter ${field.label}`}
                        name={field.name}
                        type="text"
                        labelSpan="*"
                        maxLength={field.maxLength}
                        onKeyDown={field.onKeyDown}
                        disabled={field.requireEditAccess ? editDisabled : fullView}
                        focus={!!formError?.[field.name]}
                        error={formError[field.name]}
                        value={formOrderValue?.[field.name]}
                        onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                      />
                    </div>
                  );
                })}

                {section.id === "voter-details" && (!isExistingRecord || hasInitialValue("mobile_no")) && (
                  <div className="avf-field">
                    <div className="llt_inputfield">
                      <InputField
                        floating
                        className="modal_input orderModal_Input"
                        label="Mobile Number"
                        labelClass="FormLableKaran"
                        placeholder="Enter Mobile Number"
                        name="mobile_no"
                        type="tel"
                        labelSpan="*"
                        disabled={fullView}
                        pattern="[0-9]*"
                        maxLength={10}
                        onKeyDown={handleKeyDown}
                        focus={!!formError?.mobile_no}
                        error={formError.mobile_no}
                        value={formOrderValue?.mobile_no}
                        onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                      />
                      <span className="llt_inputSpan">+91</span>
                    </div>
                  </div>
                )}

                {section.id === "address" && (!isExistingRecord || hasInitialValue("updated_address")) && (
                  <div className="avf-field avf-field-full">
                    <InputField
                      floating
                      className="modal_input orderModal_Input"
                      label="Updated Address"
                      labelClass="FormLableKaran"
                      placeholder="Enter Address"
                      name="updated_address"
                      type="text"
                      disabled={fullView}
                      focus={!!formError?.updated_address}
                      error={formError.updated_address}
                      value={formOrderValue?.updated_address}
                      onChange={({ target: { name, value } }) => handleOrderChange(name, value)}
                    />
                  </div>
                )}

                {section.id === "additional" && (
                  <>
                    {(!isExistingRecord || hasInitialValue("colour")) && (
                      <div className="avf-field">
                        <Dropdown
                          floating
                          label="Color"
                          className="dropdownoforder"
                          placeholder="Select Color"
                          name="color"
                          options={colorLIst || []}
                          valueText="value"
                          labelText="label"
                          disabled={fullView}
                          focus={!!formError?.colour}
                          error={formError?.colour}
                          selectedOption={formOrderName?.colour}
                          setSelectedOption={(data) => {
                            setFormOrderName({ ...formOrderName, colour: data });
                            handleOrderChange("colour", data?.value || "");
                          }}
                        />
                      </div>
                    )}

                    {(!isExistingRecord || hasInitialValue("profession")) && (
                      <div className="avf-field">
                        <Dropdown
                          floating
                          label="Profession"
                          className="dropdownoforder"
                          placeholder="Select Profession"
                          name="profession"
                          options={profList || []}
                          valueText="value"
                          labelText="label"
                          disabled={fullView}
                          focus={!!formError?.profession}
                          error={formError?.profession}
                          selectedOption={formOrderName?.profession}
                          setSelectedOption={(data) => {
                            setFormOrderName({ ...formOrderName, profession: data });
                            handleOrderChange("profession", data?.value || "");
                          }}
                        />
                      </div>
                    )}

                    {(!isExistingRecord || hasInitialValue("post")) && (
                      <div className="avf-field">
                        <Dropdown
                          floating
                          label="Post"
                          className="dropdownoforder"
                          placeholder="Select Post"
                          name="post"
                          options={postList || []}
                          valueText="value"
                          labelText="label"
                          disabled={fullView}
                          focus={!!formError?.post}
                          error={formError?.post}
                          selectedOption={formOrderName?.post}
                          setSelectedOption={(data) => {
                            setFormOrderName({ ...formOrderName, post: data });
                            handleOrderChange("post", data?.value || "");
                          }}
                        />
                      </div>
                    )}

                    {(!isExistingRecord || hasInitialValue("caste")) && (
                      <div className="avf-field">
                        <Dropdown
                          floating
                          label="Caste"
                          className="dropdownoforder"
                          placeholder="Select Caste"
                          name="caste"
                          options={castList || []}
                          valueText="value"
                          labelText="label"
                          isClearable
                          disabled={fullView}
                          focus={!!formError?.caste}
                          error={formError?.caste}
                          selectedOption={formOrderName?.caste}
                          setSelectedOption={(data) => {
                            setFormOrderName({ ...formOrderName, caste: data });
                            handleOrderChange("caste", data?.value || "");
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {section.id === "additional" && (
                <div className="avf-checkbox-row">
                  <div className="anvOt_inputField checkboxtext">
                    <SwitchField
                      label="Activist"
                      disabled={fullView}
                      value={formOrderValue.is_activist === true}
                      onSwitchChange={({ target: { checked } }) => handleOrderChange("is_activist", checked)}
                    />
                  </div>
                  <div className="anvOt_inputField checkboxtext">
                    <SwitchField
                      label="Voted"
                      disabled={fullView}
                      value={formOrderValue.is_voted === true}
                      onSwitchChange={({ target: { checked } }) => handleOrderChange("is_voted", checked)}
                    />
                  </div>
                </div>
              )}
            </div>
            );
          })}

          {otherDetails.length > 0 && (
            <div className="avf-section" ref={registerSectionRef("other")}>
              <div className="avf-section-head">
                <span className="avf-section-num">{SECTIONS.length + 1}</span>
                <h3 className="avf-section-title">Other Details</h3>
              </div>
              <div className="avf-grid">
                {otherDetails.map((field) => (
                  <div className="avf-field" key={field.name}>
                    <InputField
                      floating
                      className="modal_input orderModal_Input"
                      label={field.label}
                      labelClass="FormLableKaran"
                      name={field.name}
                      type="text"
                      disabled
                      value={field.value}
                      title={field.value}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(epicNo || fullView) && (
            <div className="avf-actions-row">
              <button className="addPlan" onClick={() => setShowModal(true)}>
                <FaWhatsapp style={{ fontSize: "25px", cursor: "pointer" }} />
                <span>Whatsapp</span>
              </button>
              <button className="addPlan" onClick={handleGenerateSinglePDF}>
                <FaRegFilePdf style={{ fontSize: "25px", cursor: "pointer" }} />
                <span>Download pdf</span>
              </button>
              <button className="addPlan" onClick={handleFamilyAction}>
                <GrGroup style={{ fontSize: "25px", cursor: "pointer" }} />
                <span>View Famaily Detail</span>
              </button>
              {hasAccess(userLogin?.user_type, FEATURES.DELETE_VOTER) && (
                <button className="voter-btn-danger-outline" onClick={() => setOpenDel(true)}>
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        <div className="avf-footer">
          <div className={`avf-footer-note ${requiredErrorCount > 0 ? "has-errors" : ""}`}>
            {requiredErrorCount > 0
              ? `${requiredErrorCount} of ${totalRequiredFields} required fields need attention`
              : `${totalRequiredFields} required fields`}
          </div>
          <div className="avf-footer-actions">
            <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToSearch}>
              Cancel
            </button>
            {isLoadding ? (
              <button type="button" className="avf-btn avf-btn-primary" disabled>
                Processing...
              </button>
            ) : (
              <button type="button" className="avf-btn avf-btn-primary" disabled={fullView} onClick={onOrderSubmit}>
                {isEdit ? "Update Voter" : "Create Voter"}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmModal isOpen={openDel} toggle={() => setOpenDel(!openDel)} onSubmit={handleDelete} />

      <Modal size="md" isOpen={cropModalOpen} toggle={() => setCropModalOpen(false)} centered>
        <ModalHeader toggle={() => setCropModalOpen(false)}>Crop Profile Photo</ModalHeader>
        <ModalBody>
          <div className="voter-crop-area">
            {rawImageSrc && (
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="voter-crop-zoom">
            <span>Zoom</span>
            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCropModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleCropConfirm}>
            Save Photo
          </Button>
        </ModalFooter>
      </Modal>

      <Modal size="sm" isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} centered>
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>Profile Photo</ModalHeader>
        <ModalBody className="voter-photo-preview-body">
          <img src={preview || PLACEHOLDER_AVATAR} alt="Profile full preview" className="voter-photo-preview-img" />
        </ModalBody>
      </Modal>

      <InputModal
        isOpen={showModal}
        headerName="Whatsapp Massage"
        toggle={() => setShowModal(!showModal)}
        submitLabel="Send"
        onSubmit={handleWhatsappSubmit}
        disabled={mobileNumber.length !== 12}
        inputProps={
          <InputField
            className="modal_input"
            label="Mobile Number"
            labelClass="modal_label"
            labelSpan="*"
            labelSpanClass="style_for_star"
            placeholder="Enter 10 digit Mobile Number"
            name="mobileNumber"
            maxLength={10}
            onKeyDown={handleKeyDown}
            error={whatsappError?.mobileNumber}
            value={mobileNumber.startsWith("91") ? mobileNumber.slice(2) : mobileNumber}
            onChange={({ target: { value } }) => {
              const digits = value.replace(/\D/g, "").slice(0, 10);
              setMobileNumber(digits.length === 10 ? "91" + digits : digits);
            }}
          />
        }
      />
    </>
  );
};
